import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { UsagePatterns } from '@/api/billing/billing.types';
import { Calendar, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DailyUsageChartProps {
  patterns: UsagePatterns | null;
  isLoading: boolean;
  onError: (error: string) => void;
  className?: string;
}

export const DailyUsageChart = ({ patterns, isLoading, onError, className }: DailyUsageChartProps) => {
  if (isLoading) {
    return (
      <Card className={cn("h-full shadow-sm", className)}>
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-lg" />
              <Skeleton className="h-5 w-32" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="h-[280px] relative">
            <Skeleton className="h-full w-full rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!patterns?.byDay || patterns.byDay.length === 0) {
    return (
      <Card className={cn("h-full shadow-sm", className)}>
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            <div className="space-y-0.5">
              <CardTitle className="text-base leading-none">Daily Activity</CardTitle>
              <CardDescription className="text-xs text-muted-foreground/70 leading-none">
                Recent call volume trends
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[calc(100%-73px)]">
          <div className="text-center space-y-3">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <Calendar className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">No Daily Data</p>
              <p className="text-xs text-muted-foreground">Daily activity patterns will appear here after calls are made</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Get last 14 days for display
  const recentData = patterns.byDay.slice(-14);
  const totalCalls = recentData.reduce((sum, day) => sum + (day.calls || 0), 0);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const date = new Date(label);
      const dateFormatted = date.toLocaleDateString('en', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric' 
      });
      
      return (
        <div className="bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg p-3">
          <p className="font-medium mb-1">{dateFormatted}</p>
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: payload[0].color }}
            />
            <span className="text-sm">{value.toLocaleString()} calls</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut", delay: 0.25 }}
      className={className}
    >
      <Card className="h-full shadow-sm">
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <Calendar className="h-4 w-4 text-primary" />
              </div>
              <div className="space-y-0.5">
                <CardTitle className="text-base leading-none">Daily Activity</CardTitle>
                <CardDescription className="text-xs text-muted-foreground/70 leading-none">
                  Recent call volume trends
                </CardDescription>
              </div>
            </div>
            <Badge variant="outline" className="flex items-center gap-1 text-xs">
              <TrendingUp className="h-3 w-3" />
              14 Days
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="pt-6">
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={recentData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <defs>
                  <linearGradient id="dailyGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(39 84% 56%)" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="hsl(39 84% 56%)" stopOpacity={0.4} />
                  </linearGradient>
                </defs>
                
                <XAxis 
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('en', { 
                      month: 'short', 
                      day: 'numeric' 
                    });
                  }}
                  interval={Math.ceil(recentData.length / 7)} // Show ~7 labels max
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  width={40}
                />
                
                <Tooltip content={<CustomTooltip />} />
                
                <Bar 
                  dataKey="calls" 
                  fill="url(#dailyGradient)"
                  radius={[3, 3, 0, 0]}
                  stroke="hsl(39 84% 56%)"
                  strokeWidth={1}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>


        </CardContent>
      </Card>
    </motion.div>
  );
};
